import panzoom from 'panzoom';

const debounce = require('lodash/debounce');
const DetectRTC = require('detectrtc');

import EventEmitter from '../helpers/EventEmmiter';
import MakeFloorplanScreenshots from '../service/makeFloorplanScreenshots';
import {isMetrical} from '../service/geo.service';
import {isCustomFloorplan} from 'a_common-folder/services/floorplans.service';
import {normalizeYaw} from '../e3plan-3d/modules/libs/mathematics';
import {E3CloseButton} from '../ui';

require('./e3plan-2d.scss');

class E3Plan2D extends EventEmitter {
    _classPrefix = 'e3plan-2d';
    currentFloorplan = null;
    _onMoveToScene = true;
    _currentFloorplanWidth = 0;
    _defaultHeight = 200;
    _activeFloorplan = null;
    _open = false;
    _photoSpotClickZoneEvents = [
        {
         name: 'touchstart',
         func:  this._onfloorimageselect.bind(this),
        },
        {
         name: 'mousedown',
         func:  this._onfloorimageselect.bind(this),
        },
        {
         name: 'mouseover',
         func:  this._preventZoom.bind(this),
        },
        {
         name: 'mouseleave',
         func:  this._preventZoom.bind(this),
        },
     ]

    constructor(wrapper, e3plan, options = {}) {
        super();
        this._wrapper = wrapper;
        this._e3plan = e3plan;
        this._options = options;
    }

    init(floors, markers) {
        this._createContent();
        this._addEventListener();
        this.floors = floors;
        this.markers = markers;

        if (!floors || !floors.length) {
            this._showFloorplansLoader();
            let promises = [];
            this.makeFloorplanScreenshots = new MakeFloorplanScreenshots(this._options.editable);
            promises.push(this.makeFloorplanScreenshots.loadScreen(this._e3plan));
            promises.push(isMetrical());

            Promise.all(promises)
                .then(([data, metrical]) => {
                    this.clearMarkers();
                    this.metrical = metrical;

                    let floors = [];
                    let markers = data.markers;
                    let screenShots = data.screenshots;
                    screenShots.map((res) => {
                        floors.push({
                            title: res.layerName,
                            id: res.id,
                            image_url: res.src,
                            image_url_imperial: res.image_url_imperial,
                            without_measurement: res.without_measurement,
                        })
                    });

                    this.floors = floors;
                    this.markers = markers;
                    this.imgs = this._generateImgs();

                    this._rasterFloorPlansContentInit(floors, markers, {metrical: this.metrical});
                })
                .catch((e) => {
                    debugger
                })
        } else {
            isMetrical().then((metrical) => {
                this.metrical = metrical;
                this.imgs = this._generateImgs();

                this._rasterFloorPlansContentInit(floors, markers, {metrical: this.metrical});
            })
        }
    }

    _onReady(){
        this._fireEvent('ready');
    }

    showFloorplan(floorplan = this.currentFloorplan){
        floorplan.style.opacity = 1;

        const spots = this._content.querySelectorAll('.spot');
        spots.forEach((spot) => {
            spot.style.display = 'block';
        })
    }
    
    hideFloorplan(floorplan = this.currentFloorplan){
        floorplan.style.opacity = 0;

        const spots = this._content.querySelectorAll('.spot');
        spots.forEach((spot) => {
            spot.style.display = 'none';
        })
    }

    resizeFloorplan(floorplan) {
        const {width} = floorplan.getBoundingClientRect();
        this._currentFloorplanWidth = Math.round(width);

        this._wrapper.style.width = `${this._currentFloorplanWidth}px`;
        this._hideFloorplansLoader();
        this.showFloorplan();
        this._onReady();
    }

    getObjectEl(){
        return this.currentFloorplan.querySelector('object') || this.currentFloorplan.querySelector('img')
    }

    floorplanLoad(floorplan, timeout = 200) {
        return new Promise((resolve, reject) => {
            let called = false;

            const onLoad = () => {
                if (called) return;
                
                called = true;
                resolve();
            };

            floorplan.addEventListener('load', onLoad);

            setTimeout(onLoad, timeout);
        });
    }

    resize = async (isNewPlan = false) => {
        const floorplan = this.getObjectEl();

        if(isNewPlan && floorplan){
            await this.floorplanLoad(floorplan);
        }
            
        this.resizeFloorplan(floorplan);
    }

    selectRasterPlan(sceneID){
        this.clearMarkers();
        this._options.sceneID = sceneID;
        let activeFloorplan = this._findActiveFloorplan(this.markers, this._options.sceneID);
        this._selectRasterPlan(activeFloorplan, this.markers);
        this._updateCustomPlan(activeFloorplan);
    }

    updateTeleportFOV(yaw){
        const spot = this._content.querySelector('.spot.active');
        const isCustomFloorplan = this._isCustomFloorplan();

        if(!spot || isCustomFloorplan) return false;

        const {rotateDirection} = this._findCurrentRotation();
        const shift = Math.PI/2;

        yaw = yaw * Math.PI / 180;
        yaw = normalizeYaw(yaw - rotateDirection + shift);
        yaw = yaw + shift * 3 / 2;
        yaw = yaw / Math.PI * 180;
        yaw = yaw + 180;

        if(yaw > 360) yaw = yaw - 360;

        const start = Math.abs(yaw/360*100);
        const end = start + 25;
        const colorPrimary = `grey`;
        const colorSecondary = `black`;

        let style = `conic-gradient(${colorPrimary} 0% ${start}%, ${colorSecondary} ${start}% ${end}%, ${colorPrimary} ${end}% 100%)`;

        if(end > 100){
            style = `conic-gradient(${colorSecondary} 0% ${end - 100}%, ${colorPrimary} ${end - 100}% ${start}%, ${colorSecondary} ${start}% ${100}%)`;
        }

        spot.style.backgroundImage = style;
    }

    hide(triggerEvent = false, e) {
        if (e){
            e.preventDefault();
            e.stopPropagation();
        }

        this.moveScene(false);
        this._wrapper.classList.add('hidden');
        this._removeEventListener();
        this._fireEvent('destroy');
    }

    _runZoom() {
        let content = this._content;
        const instance = panzoom(content, {
            minZoom: 0.5,
            bounds: true,
            boundsPadding: 0.3
        });

        this.delayTimeout = null;
        this.moveScene = (move) => {
            this._onMoveToScene = move;
            this.clearMoveScene();
        };
        this.clearMoveScene =  debounce(() => {
            this._onMoveToScene = true;
        }, 100)

        instance.on('panstart', (e) => this._onMoveToScene = false);

        instance.on('panend', (e) => this.moveScene(false));

        instance.on('zoom', (e) => this.moveScene(false));

        instance.on('zoomend', (e) => this.moveScene(false));

        instance.on('panzoomchange', (e) => this.moveScene(false));
    }

    _initCloseButton() {
        const close = new E3CloseButton(this._wrapper, {
            classNames: ['absolute-top-right'],
            events: {
                'mousedown': this.hide.bind(this, true),
                'touchstart': this.hide.bind(this, true),
                'mouseover': this._preventZoom,
                'mouseenter': this._preventZoom,
                'mouseleave': this._preventZoom,
            }
        });

        close.show();

        if(DetectRTC.isMobileDevice){
            close.element().style.opacity = 1;
        }

        return close;
    }

     _findCurrentRotation(){
        let angle = 0;
        let rotateDirection = 0;
        const layers = Object.values(this._e3plan.preview.layers);

        for(let i=0; i<layers.length; i++){
            const areas = Object.values(layers[i].areas);

            for(let y=0; y<areas.length; y++){
                const area = areas[y];
                const id = area.properties.scene.id;

                if(id == this._options.sceneID){
                    angle = area.properties.scene.angle;
                    rotateDirection = area.properties.scene.rotateDirection;
                }

            }
        }

        return {angle, rotateDirection};
     }
 
     clearMarkers() {
        const markers = this._content.querySelectorAll('.spot');
        markers.forEach((marker) => {
            this._content.removeChild(marker);
        })
    }

    _eventClick = () => {
        if(this._onMoveToScene){
            this._fireEvent('click');
        }
    }

    _eventMouseOver = () => {
        if(this._open) return ;

        this._open = true;
        const width = this._currentFloorplanWidth;
        const focusWidth = width * 2;
        this._wrapper.style.width = `${focusWidth}px`;

        const {height} = this._wrapper.getBoundingClientRect();
        const focusHeight = height * 2;
        this._wrapper.style.height =`${focusHeight}px`;
        this._wrapper.style.minHeight = `${focusHeight}px`;
    }

    _eventMouseLeave = () => {
        this._open = false;
        const width = this._currentFloorplanWidth;
        this._wrapper.style.width = `${Math.round(width)}px`;

        this._wrapper.style.height =`${this._defaultHeight}px`;
        this._wrapper.style.minHeight = `${this._defaultHeight}px`;

        setTimeout(() => {
            this.selectRasterPlan(this._options.sceneID);
            this._runZoom();
        }, 200)
    }

    _addEventListener(){
        this._wrapper.addEventListener('touchstart', this._eventClick.bind(this));
        this._wrapper.addEventListener('mouseup', this._eventClick.bind(this));
        this._wrapper.addEventListener('mouseover', this._eventMouseOver.bind(this));
        this._wrapper.addEventListener('mouseleave', this._eventMouseLeave.bind(this));
    }

    _removeEventListener(){
        this._wrapper.removeEventListener('touchstart', this._eventClick.bind(this));
        this._wrapper.removeEventListener('mouseup', this._eventClick.bind(this));
        this._wrapper.removeEventListener('mouseover', this._eventMouseOver.bind(this));
        this._wrapper.removeEventListener('mouseleave', this._eventMouseLeave.bind(this));
    }

    _createContent(wrapper){
        const el = document.createElement('div');
        el.classList.add('content');

        this._content = el;
        this._wrapper.appendChild(this._content);
    }

    _appendFloorplan(container, floorplan) {
        const prevFloorplan = container.querySelector('.img-loader');
        if(prevFloorplan) container.removeChild(prevFloorplan);

        container.appendChild(floorplan);
    }

    _isCustomFloorplan(activeFloorplan = this._activeFloorplan){
        if(!activeFloorplan) return true;
        
        return this.floors.find(({id}) => id === activeFloorplan).image_url.match(/^(http|https):\/\/[^ "]+|(.*floorplan_thumbnails.*)$/);
    }

    _updateCustomPlan(activeFloorplan){
        this._wrapper.classList.remove('custom-floorplan');

        const isCustomFloorplan = this._isCustomFloorplan(activeFloorplan);
        if(isCustomFloorplan) this._wrapper.classList.add('custom-floorplan');

        return isCustomFloorplan;
    }

    _selectRasterPlan(value, markers) {
        markers = markers.filter((marker) => marker.floorplanID === value);
        this._content.style.transform = "none";
        const container = this._content;
        const floorplan =  this.imgs[value];
        this.hideFloorplan(floorplan);
        this._showFloorplansLoader();
        let photoSpot, i;
        const isNewPlan = this.currentFloorplan !== floorplan;

        if(isNewPlan) this._appendFloorplan(container, floorplan);

        this.currentFloorplan = floorplan;

        const activeScene = this._options.sceneID;

        for (i = 0; i < markers.length; i++) {
            photoSpot = document.createElement('span');
            photoSpot.style.left = markers[i].x + '%';
            photoSpot.style.top = markers[i].y + '%';
            photoSpot.style.display = 'none';
            photoSpot.dataset.sceneId = markers[i].imageID;
            photoSpot.classList.add('spot');
            if (activeScene && activeScene === markers[i].imageID) {
                photoSpot.classList.add('active');
                this._activeFloorplan = markers[i].floorplanID;
            }

            const photoSpotClickZone = document.createElement('span');
            photoSpotClickZone.classList.add('clickablezone');
            this._photoSpotClickZoneEvents.forEach(({name, func}) => {
                photoSpotClickZone.addEventListener(name, func);
            })
           
            photoSpot.appendChild(photoSpotClickZone);

            container.appendChild(photoSpot);
        }

        this.resize(isNewPlan);

        photoSpot = null;
    }

    _preventZoom(e) {
        if(e){
            e.preventDefault();
            e.stopPropagation();
        }
    }

    _onfloorimageselect(e){
        this._preventZoom(e);

        this.moveScene(false);

        const sceneId = e.currentTarget.parentElement.dataset.sceneId;

        this._fireEvent('scenes.select', sceneId);
    }

    _showFloorplansLoader() {
        let block = document.createElement('div');
        block.classList.add('loading')

        this._content.appendChild(block);
    }

    _hideFloorplansLoader() {
        const loader = this._content.querySelector('.loading');
        if(loader) this._content.removeChild(loader);
    }

    _generateImgs() {
        let floors = this.floors;
        let imgs = {}, imgEl, content;
        for (var i = 0; i < floors.length; i++) {
            const imgEl = this._createImageObj();
            imgEl.crossOrigin = 'anonymous';
            content = document.createElement('div');
            content.classList.add('img-loader');
            content.classList.add('img-loads');
            content.appendChild(imgEl);
            imgEl.addEventListener('load', function (content, image) {
                image.style.width = 'auto';
                image.style.height = 'auto';
                content.dataset.width = image.width || image.clientWidth;
                content.dataset.height = image.height || image.clientHeight;
                content.dataset.aspect = content.dataset.width / content.dataset.height;
                content.classList.remove('img-loads');
                image.style.width = '';
                image.style.height = '';
            }.bind(null, content, imgEl));

            let src = this.metrical ? floors[i].image_url : floors[i].image_url_imperial || floors[i].image_url;

            if(this._options.permissions['dimensions'] === false && floors[i].without_measurement){
                src = floors[i].without_measurement;
            }

            imgEl.src = src;
            imgs[floors[i].id] = content;
        }
        return imgs;
    }

    _createImageObj() {
        let imgEl = new Image();

        if(!isCustomFloorplan(this.floors)) {
            imgEl = document.createElement('object');
            imgEl.type = "image/svg+xml";

            Object.defineProperty(imgEl, "src", {
                set(src) {
                    imgEl.data = src;
                },
            });
        }

        return imgEl;
    }

    _setClass(elem, className) {
        elem.classList.add(this._classPrefix + (className ? ('-' + className) : ''));
    }

    _findActiveFloorplan(markers, sceneId, floors = this.floors) {
        var i;
        if (!sceneId) {
            return null;
        }
        for (i = 0; i < markers.length; i++) {
            if (markers[i].imageID == sceneId) {
                return markers[i].floorplanID;
            }
        }
        return floors[0].id;
    }

    _rasterFloorPlansContentInit(floors, markers) {
        let activeFloorplan = this._findActiveFloorplan(markers, this._options.sceneID, floors);
        this._hideFloorplansLoader();
        this._selectRasterPlan(activeFloorplan, markers);
        this._runZoom();
        this._initCloseButton();
        this._updateCustomPlan(activeFloorplan);
    }
}

export default E3Plan2D;